import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  InputNumber,
  Flex,
  Grid,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  fetchRecurringJournals,
  saveRecurringJournal,
} from "./recurringJournalThunk";
import {
  setSelectedRecurring,
  setIsEditRecurring,
  setRefreshRecurringList,
} from "./recurringJournalSlice";

import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import InputNumberField from "../../components/Input/InputNumber";
import { dateFormat, getAccessType } from "../../lib/functions";
import { fetchHeadAccs } from "../Journal/journalThunk";

const { Title }      = Typography;
const { TextArea }   = Input;
const { useBreakpoint } = Grid;

const FREQUENCY_OPTIONS = [
  { label: "Day",   value: "day"   },
  { label: "Week",  value: "week"  },
  { label: "Month", value: "month" },
  { label: "Year",  value: "year"  },
];

const RecurringJournalEntry = () => {
  const { modal }  = App.useApp();
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const screens    = useBreakpoint();
  const [form]     = Form.useForm();

  const { selectedRecurring, isEdit } = useSelector(
    (state) => state.recurringJournals
  );
  const { headAccs } = useSelector((state) => state.journals);

  const [rows,        setRows]        = useState([]);
  const [access_type, setAccessType]  = useState("");

  useEffect(() => {
    const access = getAccessType("journal_entry");
    setAccessType(access);
  }, []);

  // load accounts
  useEffect(() => {
    dispatch(fetchHeadAccs());
    setRows([
      { key: Date.now() + Math.random(), acc_id: "", acc_name: "", debit_amount: 0, credit_amount: 0 },
      { key: Date.now() + Math.random(), acc_id: "", acc_name: "", debit_amount: 0, credit_amount: 0 },
    ]);
  }, [dispatch]);

  // fill form on edit
  useEffect(() => {
    if (isEdit && selectedRecurring) {
      form.setFieldsValue({
        narration:      selectedRecurring.narration,
        invoice_no:     selectedRecurring.invoice_no,
        frequency:      selectedRecurring.frequency,
        interval_value: selectedRecurring.interval_value,
        start_date:     dayjs(selectedRecurring.start_date),
        end_date:       selectedRecurring.end_date
          ? dayjs(selectedRecurring.end_date)
          : null,
      });

      setRows(
        (selectedRecurring.rows_json || []).map((r) => ({
          key:           r.acc_id + Math.random(),
          acc_id:        r.acc_id,
          acc_name:      r.acc_name || "",
          debit_amount:  r.debit_amount  || 0,
          credit_amount: r.credit_amount || 0,
        }))
      );
    }
  }, [isEdit, selectedRecurring, form]);

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key:           Date.now() + Math.random(),
        acc_id:        "",
        acc_name:      "",
        debit_amount:  0,
        credit_amount: 0,
      },
    ]);
  };

  const handleRemoveRow = (key) =>
    setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedRecurring(null));
    dispatch(setIsEditRecurring(false));
    navigate("/accounts/recurring-journals");
  };

  const onFinish = async (values) => {
    // validate rows
    for (const r of rows) {
      if (!r.acc_id) {
        modal.error({ title: "Please select an account for all rows." });
        return;
      }
    }

    const debitTotal  = rows.reduce((s, r) => s + (Number(r.debit_amount)  || 0), 0);
    const creditTotal = rows.reduce((s, r) => s + (Number(r.credit_amount) || 0), 0);

    if (debitTotal !== creditTotal) {
      modal.error({ title: "Debit and Credit totals must match." });
      return;
    }

    if (debitTotal === 0) {
      modal.error({ title: "Amounts cannot be zero." });
      return;
    }

    const rows_json = rows.map((r) => ({
      acc_id:        r.acc_id,
      acc_name:      r.acc_name,
      debit_amount:  Number(r.debit_amount)  || 0,
      credit_amount: Number(r.credit_amount) || 0,
    }));

    const payload = {
      action:         isEdit ? "update" : "create",
      id:             isEdit ? selectedRecurring.id : undefined,
      narration:      values.narration      || "",
      invoice_no:     values.invoice_no     || "",
      frequency:      values.frequency,
      interval_value: values.interval_value || 1,
      start_date:     values.start_date.format("YYYY-MM-DD"),
      end_date:       values.end_date
        ? values.end_date.format("YYYY-MM-DD")
        : null,
      rows_json:      JSON.stringify(rows_json),
    };

    try {
      const res = await dispatch(saveRecurringJournal(payload)).unwrap();
      modal.success({ title: res.msg || "Saved successfully." });

      dispatch(setSelectedRecurring(null));
      dispatch(setIsEditRecurring(false));
      dispatch(setRefreshRecurringList(true));
      form.resetFields();
      setRows([]);
      navigate("/accounts/recurring-journals");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save." });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Account",
      render: (_, record, index) => (
        <SearchListAutoComplete
          data-testid={`account-select-${index}`}
          entityType="accounts"
          style={{ width: "100%" }}
          optionsList={(headAccs || []).map((acc) => ({
            sale_id:      acc.acc_id,
            display_text: acc.acc_name,
            raw:          acc,
          }))}
          value={
            record.acc_id
              ? { value: record.acc_id, display_text: record.acc_name }
              : null
          }
          onSelect={(obj) => {
            const newRows = [...rows];
            newRows[index].acc_id   = obj?.raw?.acc_id   || "";
            newRows[index].acc_name = obj?.raw?.acc_name || "";
            setRows(newRows);
          }}
          placeholder="Account"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Debit Amount",
      render: (_, record, index) => (
        <InputNumberField
        data-testid={`debit-input-${index}`}
          min={0}
          step={1}
          precision={2}
          value={record.debit_amount}
          style={{ width: "100%" }}
          onFocus={() => {
            const newRows = [...rows];
            newRows[index].debit_amount = undefined;
            setRows(newRows);
          }}
          onChange={(val) => {
            const newRows = [...rows];
            const v = Number(val) || 0;
            if (v > 0) newRows[index].credit_amount = 0;
            newRows[index].debit_amount = v;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Credit Amount",
      render: (_, record, index) => (
        <InputNumberField
        data-testid={`credit-input-${index}`}
          min={0}
          step={1}
          precision={2}
          value={record.credit_amount}
          style={{ width: "100%" }}
           onFocus={() => {
            const newRows = [...rows];
            newRows[index].credit_amount = undefined;
            setRows(newRows);
          }}
          onChange={(val) => {
            const newRows = [...rows];
            const v = Number(val) || 0;
            if (v > 0) newRows[index].debit_amount = 0;
            newRows[index].credit_amount = v;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRow(record.key)}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>
        {isEdit ? "Edit Recurring Journal" : "Add Recurring Journal"}
      </Title>

      <Form
        form={form}
        layout="horizontal"
        variant="filled"
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        style={{ maxWidth: 1400 }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md ? "column" : "row",
            width:         "100%",
            marginBottom:  24,
          }}
        >
          {/* LEFT COLUMN */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md ? "100%" : "40%" }}
          >
            <Form.Item
              name="narration"
              label="Narration"
            >
              <TextArea rows={2} placeholder="e.g. Monthly office rent" />
            </Form.Item>

            <Form.Item
              name="invoice_no"
              label="Invoice No."
            >
              <Input placeholder="Invoice No." />
            </Form.Item>

         <Form.Item
  name="start_date"
  label="Start Date"
  initialValue={dayjs()}
  rules={[{ required: true, message: "Please select start date" }]}
>
  <DatePicker
    style={{ width: "100%" }}
    format="DD/MM/YYYY"    // ← hardcoded instead of dateFormat
    disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
  />
</Form.Item>

<Form.Item
  name="end_date"
  label="End Date"
  extra="Leave empty to repeat forever"
>
  <DatePicker 
    style={{ width: "100%" }} 
    format="DD/MM/YYYY"    // ← hardcoded instead of dateFormat
  />
</Form.Item>
          </Flex>

          {/* RIGHT COLUMN */}
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md ? "100%" : "40%" }}
          >
            <Form.Item
              label="Frequency"
              style={{ marginBottom: 0 }}
              required
            >
              <Flex gap={8}>
                <Form.Item
                  name="frequency"
                  rules={[{ required: true, message: "Required" }]}
                  style={{ flex: 2, marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select frequency"
                    options={FREQUENCY_OPTIONS}
                  />
                </Form.Item>

                <Form.Item
                  name="interval_value"
                  initialValue={1}
                  rules={[{ required: true, message: "Required" }]}
                  style={{ flex: 1, marginBottom: 0 }}
                  extra="Every N"
                >
                  <InputNumber min={1} style={{ width: "100%" }}  onFocus={() => { form.setFieldsValue({ interval_value: undefined, }); }}/>
                </Form.Item>
              </Flex>
            </Form.Item>

            <Form.Item label=" ">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Examples: frequency=day, N=5 → every 5 days.
                frequency=month, N=1 → every month.
              </Typography.Text>
            </Form.Item>
          </Flex>
        </Flex>

        {/* JOURNAL ROWS */}
        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
          icon={<PlusOutlined />}
        >
          Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="key"
          bordered
          size="middle"
          scroll={{ x: "max-content" }}
          style={{ marginBottom: 24 }}
          footer={() => {
            const debitTotal  = rows.reduce((s, r) => s + Number(r.debit_amount  || 0), 0);
            const creditTotal = rows.reduce((s, r) => s + Number(r.credit_amount || 0), 0);
            return (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <strong>
                  Debit Total: {debitTotal.toFixed(2)} | Credit Total:{" "}
                  {creditTotal.toFixed(2)}
                </strong>
              </div>
            );
          }}
        />

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              setRows([]);
            }}
          >
            Reset
          </Button>
          {access_type === "WRITE" && (
            <Button
  type="primary"
  htmlType="submit"
  data-testid="btn-save-recurring-journal"
>
  {isEdit ? "Update" : "Save"}
</Button>
          )}
        </Space>
      </Form>
    </div>
  );
};

export default RecurringJournalEntry;